import React, { useEffect, useMemo } from 'react';
import { Layout, notification } from 'antd';
import { withTranslation } from 'react-i18next';
import { CheckoutWrapper } from '../../styles/checkoutStyles';
import CampaignHeader from './CampaignHeader';
import CampaignMetrics from './CampaignMetrics';
import MapAndScreensList from './MapAndScreensList';
import PerformanceHighlights from './PerformanceHighlights';
import AudienceCharts from './AudienceCharts';
import InvestmentCharts from './InvestmentCharts';
import useCheckoutAudienceData from '../../hooks/contentManager/useCheckoutAudienceData';
import { isCpmiCompany } from '../../../../../services/utils';

const { Content } = Layout;

const CheckoutSummaryView = ({
  campaign,
  loading,
  audienceData,
  subtotalCampaign,
  totalCampaign,
  currencyCampaign,
  pricesData,
  client,
  cart,
  user,
  t
}) => {
  if (!audienceData) return null;

  return (
    <div className="checkoutContent">
      <CampaignHeader name={campaign.name} type={t('Programmatic campaign')} />
      <div style={{ marginTop: '20px' }}>
        <CampaignMetrics
          campaign={campaign}
          loading={loading}
          audienceData={audienceData.metrics || {}}
          subtotalCampaign={subtotalCampaign}
          totalCampaign={totalCampaign}
          currencyCampaign={currencyCampaign}
          client={client}
          cart={cart}
          isCpmi={isCpmiCompany(user)}
        />
      </div>
      <div style={{ marginTop: '20px' }}>
        <MapAndScreensList campaign={campaign} pricesData={pricesData} cart={cart} />
      </div>
      <div style={{ marginTop: '20px' }}>
        <PerformanceHighlights loading={loading} data={audienceData.performance || {}} />
      </div>
      <div style={{ marginTop: '20px' }}>
        <AudienceCharts
          loading={loading}
          socioEconomicData={audienceData.charts ? audienceData.charts.socioEconomic : null}
          genderData={audienceData.charts ? audienceData.charts.gender : null}
          ageRangeData={audienceData.charts ? audienceData.charts.ageRange : null}
        />
      </div>
      <div style={{ marginTop: '20px' }}>
        <InvestmentCharts
          loading={loading}
          investmentByTypeData={audienceData.charts ? audienceData.charts.investmentByType : null}
          investmentBySubtypeData={
            audienceData.charts ? audienceData.charts.investmentBySubtype : null
          }
          investmentByMediaOwnerData={
            audienceData.charts ? audienceData.charts.investmentByMediaOwner : null
          }
        />
      </div>
    </div>
  );
};

const CheckoutComponent = ({
  audience,
  programmatic,
  amountBudget,
  date_from,
  date_to,
  currentCampaign,
  pricesData,
  user,
  creationType,
  cart,
  customContents,
  editCampaignActive,
  proposalAudience,
  campaignAudienceAttributes,
  getProposalAudience,
  getAudience,
  getCampaignAudienceAttributes,
  paymentLoading,
  campaignData,
  getProposal,
  t,
  client,
  campaign
}) => {
  const effectiveCampaign = useMemo(() => {
    if (campaignData) return campaignData;
    if (campaign && campaign.id) return campaign;
    return null;
  }, [campaign, campaignData]);

  const { total_price_with_profit, tax, currency } = pricesData;
  const campaignId = editCampaignActive ? currentCampaign.id : null;
  const currencyCampaign = currency;
  const subtotalCampaign = total_price_with_profit;
  const totalCampaign =
    parseFloat(total_price_with_profit) +
    (parseFloat(total_price_with_profit) * parseFloat(tax)) / 100;

  const displayIds =
    effectiveCampaign && effectiveCampaign.contents_displays
      ? [...effectiveCampaign.contents_displays.map(d => d.display_id)].sort().join(',')
      : '';

  useEffect(() => {
    if (!effectiveCampaign || !effectiveCampaign.contents_displays) return;

    const displayIdsArray = effectiveCampaign.contents_displays.map(d => d.display_id || d.id);
    const uniqueDisplayIds = [...new Set(displayIdsArray)];
    if (uniqueDisplayIds.length > 0) {
      getCampaignAudienceAttributes(uniqueDisplayIds);
    }

    const startDateIso = date_from ? date_from.split(' ')[0] : null;
    const endDateIso = date_to ? date_to.split(' ')[0] : null;

    // pricesData.items son relaciones (display_id repetido). Agrupamos por pantalla y
    // sumamos las salidas para no enviar display_id duplicados a la API de audiencia
    // (evita inflar impactos/alcance cuando una pantalla tiene varias versiones).
    const audienceDisplays = [];
    if (pricesData && pricesData.items) {
      const showsByDisplay = {};
      pricesData.items.forEach(item => {
        const id = item.display_id;
        if (showsByDisplay[id] === undefined) {
          showsByDisplay[id] = { id, promised_shows: 0 };
        }
        showsByDisplay[id].promised_shows +=
          (item.calculations_info && item.calculations_info.promised_shows_with_discount) || 0;
      });
      Object.values(showsByDisplay).forEach(d => {
        audienceDisplays.push({ id: d.id, promised_shows: d.promised_shows || null });
      });
    }

    const audiencePayload = {
      _method: 'GET',
      start_date: startDateIso,
      end_date: endDateIso,
      content_displays: null,
      displays: audienceDisplays,
      type: programmatic === 'programmatic' ? 'programmatic' : 'traditional',
      external_dsp_id: effectiveCampaign.external_dsp_id
    };
    getAudience(audiencePayload);

    let proposalAudiencePayload;
    if (Boolean(campaignId)) {
      proposalAudiencePayload = { campaign_id: campaignId };
    } else {
      const startWithTime = startDateIso ? `${startDateIso} 00:00:00` : null;
      const endWithTime = endDateIso ? `${endDateIso} 23:59:59` : null;
      proposalAudiencePayload = {
        contentDisplays: effectiveCampaign.contents_displays.map(d => ({
          display_id: d.display_id || d.id,
          rules: d.rules || []
        })),
        startDate: startWithTime,
        endDate: endWithTime
      };
    }
    getProposalAudience(proposalAudiencePayload);
  }, [
    campaignId,
    date_to,
    date_from,
    displayIds,
    programmatic,
    customContents,
    editCampaignActive,
    getProposalAudience,
    getCampaignAudienceAttributes,
    getAudience
  ]);

  const { audienceData } = useCheckoutAudienceData({
    effectiveCampaign,
    audience,
    proposalAudience,
    campaignAudienceAttributes,
    pricesData,
    programmatic,
    amountBudget,
    cart,
    date_from,
    date_to,
    t
  });

  const handleFinishProposal = () => {
    if (creationType === 'makeProposal') {
      getProposal({ campaign_id: campaignId });
      notification.info({ message: 'Generando Propuesta', duration: 8 });
    }
  };

  return (
    <Layout style={{ backgroundColor: 'transparent' }}>
      <Content>
        <div style={{ margin: '0 auto' }}>
          <CheckoutWrapper>
            <div className="checkoutInner">
              <CheckoutSummaryView
                campaign={effectiveCampaign}
                user={user}
                loading={paymentLoading}
                audienceData={audienceData}
                isProposal={creationType === 'makeProposal'}
                onFinishProposal={handleFinishProposal}
                totalCampaign={totalCampaign}
                subtotalCampaign={subtotalCampaign}
                currencyCampaign={currencyCampaign}
                pricesData={pricesData}
                client={client}
                cart={cart}
                t={t}
              />
            </div>
          </CheckoutWrapper>
        </div>
      </Content>
    </Layout>
  );
};

export default withTranslation()(CheckoutComponent);
